import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Loader2, Plus, DollarSign, Trash2, Pencil, ArrowLeft } from 'lucide-react'
import { SimpleIcon } from '@adthlete/common'

// Platform and territory options
const PLATFORMS = [
  { value: 'youtube', label: 'YouTube', slug: 'youtube', color: '#FF0000' },
  { value: 'tiktok', label: 'TikTok', slug: 'tiktok', color: '#000000' },
  { value: 'instagram', label: 'Instagram', slug: 'instagram', color: '#E4405F' },
] as const

const USAGE_PERMISSIONS = ['Website', 'Email', 'SMS'] as const

const TERRITORIES = ['Worldwide', 'United States', 'North America', 'Europe', 'APAC'] as const

interface PlatformOffer {
  platform: 'youtube' | 'tiktok' | 'instagram'
  usage_permission: string[]
  territory: string
  usage_term_days: number
  fee: number
}

interface LicensingOfferDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: (offer: { platforms: PlatformOffer[]; message: string }) => Promise<void>
  athleteName: string
}

type Step = 'add' | 'summary'

export const LicensingOfferDialog: React.FC<LicensingOfferDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
  athleteName,
}) => {
  const [step, setStep] = useState<Step>('add')
  const [platforms, setPlatforms] = useState<PlatformOffer[]>([])
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Form state for adding/editing a platform
  const [formPlatforms, setFormPlatforms] = useState<string[]>([])
  const [formPlatform, setFormPlatform] = useState<'youtube' | 'tiktok' | 'instagram'>('youtube')
  const [formUsageTermDays, setFormUsageTermDays] = useState('30')
  const [formUsagePermission, setFormUsagePermission] = useState<string[]>([])
  const [formTerritory, setFormTerritory] = useState('Worldwide')
  const [formCustomTerritory, setFormCustomTerritory] = useState('')
  const [formFee, setFormFee] = useState('')

  const resetForm = () => {
    setFormPlatforms([])
    setFormPlatform('youtube')
    setFormUsageTermDays('30')
    setFormUsagePermission([])
    setFormTerritory('Worldwide')
    setFormCustomTerritory('')
    setFormFee('')
    setEditIndex(null)
  }

  const handleClose = () => {
    if (!submitting) {
      setPlatforms([])
      setMessage('')
      setError(null)
      setStep('add')
      resetForm()
      onOpenChange(false)
    }
  }

  const toggleFormPlatform = (value: string) => {
    setFormPlatforms((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value],
    )
  }

  const handleAddPlatform = () => {
    const fee = parseFloat(formFee)
    const usageTermDays = parseInt(formUsageTermDays, 10)

    if (isNaN(fee) || fee <= 0) {
      setError('Please enter a valid licensing fee')
      return
    }
    if (isNaN(usageTermDays) || usageTermDays <= 0) {
      setError('Please enter a valid usage term')
      return
    }

    const territory = formTerritory === 'custom' ? formCustomTerritory : formTerritory
    if (!territory) {
      setError('Please select or enter a territory')
      return
    }

    if (editIndex !== null) {
      // Editing a single existing platform entry
      const updated = [...platforms]
      updated[editIndex] = {
        platform: formPlatform,
        usage_permission: formUsagePermission,
        territory,
        usage_term_days: usageTermDays,
        fee,
      }
      setPlatforms(updated)
    } else {
      // Adding new — require at least one platform selected
      if (formPlatforms.length === 0) {
        setError('Please select at least one platform')
        return
      }

      // Filter out platforms already added
      const toAdd = formPlatforms.filter(
        (p) => !platforms.some((existing) => existing.platform === p),
      )
      const duplicates = formPlatforms.filter((p) =>
        platforms.some((existing) => existing.platform === p),
      )
      if (duplicates.length > 0 && toAdd.length === 0) {
        setError(
          `${duplicates.map((d) => PLATFORMS.find((p) => p.value === d)?.label).join(', ')} already added`,
        )
        return
      }

      const newEntries: PlatformOffer[] = toAdd.map((p) => ({
        platform: p as 'youtube' | 'tiktok' | 'instagram',
        usage_permission: formUsagePermission,
        territory,
        usage_term_days: usageTermDays,
        fee,
      }))
      setPlatforms([...platforms, ...newEntries])
    }

    setError(null)
    resetForm()
    setStep('summary')
  }

  const handleEditPlatform = (index: number) => {
    const platform = platforms[index]
    setFormPlatform(platform.platform)
    setFormUsageTermDays(platform.usage_term_days.toString())
    setFormUsagePermission(platform.usage_permission)

    if (TERRITORIES.includes(platform.territory as any)) {
      setFormTerritory(platform.territory)
      setFormCustomTerritory('')
    } else {
      setFormTerritory('custom')
      setFormCustomTerritory(platform.territory)
    }

    setFormFee(platform.fee.toString())
    setEditIndex(index)
    setStep('add')
  }

  const handleRemovePlatform = (index: number) => {
    setPlatforms(platforms.filter((_, i) => i !== index))
  }

  const handleConfirm = async () => {
    if (platforms.length === 0) {
      setError('Please add at least one platform')
      return
    }

    setSubmitting(true)
    setError(null)

    try {
      await onConfirm({ platforms, message })
      handleClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send licensing offer')
    } finally {
      setSubmitting(false)
    }
  }

  const totalFee = platforms.reduce((sum, p) => sum + p.fee, 0)

  const togglePermission = (permission: string) => {
    setFormUsagePermission((prev) =>
      prev.includes(permission) ? prev.filter((p) => p !== permission) : [...prev, permission],
    )
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Book a license</DialogTitle>
          <DialogDescription>
            Contact <strong>{athleteName}</strong> about licensing additional social platforms.
          </DialogDescription>
        </DialogHeader>

        {step === 'add' && (
          <div className="space-y-4 py-2">
            {editIndex !== null && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  resetForm()
                  setStep('summary')
                }}
                className="mb-2"
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to Summary
              </Button>
            )}

            {/* Platform Selection */}
            <div className="space-y-2">
              <Label>{editIndex !== null ? 'Platform' : 'Platforms'}</Label>
              {editIndex !== null ? (
                <Select value={formPlatform} onValueChange={(v: any) => setFormPlatform(v)}>
                  <SelectTrigger>
                    <SelectValue>
                      {PLATFORMS.find((p) => p.value === formPlatform)?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex flex-col gap-2">
                  {PLATFORMS.map((p) => (
                    <div key={p.value} className="flex items-center gap-2">
                      <Checkbox
                        id={`form-platform-${p.value}`}
                        checked={formPlatforms.includes(p.value)}
                        onCheckedChange={() => toggleFormPlatform(p.value)}
                        disabled={platforms.some((existing) => existing.platform === p.value)}
                      />
                      <label
                        htmlFor={`form-platform-${p.value}`}
                        className={`text-sm ${platforms.some((existing) => existing.platform === p.value) ? 'text-muted-foreground line-through' : 'cursor-pointer'}`}
                      >
                        {p.label}
                        {platforms.some((existing) => existing.platform === p.value) &&
                          ' (already added)'}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Usage Term */}
            <div className="space-y-2">
              <Label>Usage Term: How many days should this campaign run?</Label>
              <Input
                type="number"
                min="1"
                placeholder="e.g., 30"
                value={formUsageTermDays}
                onChange={(e) => setFormUsageTermDays(e.target.value)}
              />
            </div>

            {/* Usage Permission */}
            <div className="space-y-2">
              <Label>Usage Permission</Label>
              <div className="flex flex-col gap-2">
                {USAGE_PERMISSIONS.map((permission) => (
                  <div key={permission} className="flex items-center gap-2">
                    <Checkbox
                      id={`permission-${permission}`}
                      checked={formUsagePermission.includes(permission)}
                      onCheckedChange={() => togglePermission(permission)}
                    />
                    <label htmlFor={`permission-${permission}`} className="text-sm">
                      {permission}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Licensing Fee */}
            <div className="space-y-2">
              <Label>Licensing Fee</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="number"
                  min="1"
                  step="0.01"
                  placeholder="250"
                  value={formFee}
                  onChange={(e) => setFormFee(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* Territory */}
            <div className="space-y-2">
              <Label>Territory</Label>
              <div className="flex flex-col gap-2">
                {TERRITORIES.map((territory) => (
                  <div key={territory} className="flex items-center gap-2">
                    <Checkbox
                      id={`territory-${territory}`}
                      checked={formTerritory === territory}
                      onCheckedChange={() => setFormTerritory(territory)}
                    />
                    <label htmlFor={`territory-${territory}`} className="text-sm">
                      {territory}
                    </label>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="territory-custom"
                    checked={formTerritory === 'custom'}
                    onCheckedChange={() => setFormTerritory('custom')}
                  />
                  <label htmlFor="territory-custom" className="text-sm">
                    Country-specific (free text)
                  </label>
                </div>
                {formTerritory === 'custom' && (
                  <Input
                    placeholder="Enter specific countries..."
                    value={formCustomTerritory}
                    onChange={(e) => setFormCustomTerritory(e.target.value)}
                    className="ml-6"
                  />
                )}
              </div>
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button className="w-full" onClick={handleAddPlatform}>
              {editIndex !== null
                ? 'Update License'
                : formPlatforms.length > 1
                  ? `Add ${formPlatforms.length} Platforms to Offer`
                  : 'Add to Offer'}
            </Button>
          </div>
        )}

        {step === 'summary' && (
          <div className="space-y-4 py-2">
            {/* Platform List */}
            {platforms.map((platform, index) => (
              <div key={index} className="rounded-lg border p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <SimpleIcon
                      slug={PLATFORMS.find((p) => p.value === platform.platform)?.slug || ''}
                      height={20}
                      width={20}
                    />
                    <span className="font-medium">
                      {PLATFORMS.find((p) => p.value === platform.platform)?.label}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemovePlatform(index)}
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      Remove
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleEditPlatform(index)}>
                      <Pencil className="mr-1 h-4 w-4" />
                      Edit
                    </Button>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm text-muted-foreground">
                  <div>
                    <span className="font-medium text-foreground">Usage Permission:</span>{' '}
                    {platform.usage_permission.length > 0
                      ? platform.usage_permission.join(' + ')
                      : 'None specified'}
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Fee:</span> $
                    {platform.fee.toFixed(2)}
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Duration:</span>{' '}
                    {platform.usage_term_days} days
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Territory:</span>{' '}
                    {platform.territory}
                  </div>
                </div>
              </div>
            ))}

            {/* Add Another Button - only when not all platforms are covered */}
            {platforms.length < PLATFORMS.length && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  resetForm()
                  setStep('add')
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Another Platform
              </Button>
            )}

            {/* Message */}
            <div className="space-y-2">
              <Textarea
                placeholder="Hi Alex! I've shared an additional licensing below. Take a look when you have a moment, and feel free to reach out with any questions."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
              />
            </div>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter className="flex-col gap-2 sm:flex-row">
          {step === 'summary' && (
            <>
              <div className="flex-1 text-sm text-muted-foreground">
                Total:{' '}
                <span className="font-semibold text-foreground">${totalFee.toFixed(2)}</span>
              </div>
              <Button variant="outline" onClick={handleClose} disabled={submitting}>
                Save as Draft
              </Button>
              <Button onClick={handleConfirm} disabled={submitting || platforms.length === 0}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Book license'
                )}
              </Button>
            </>
          )}
          {step === 'add' && platforms.length > 0 && editIndex === null && (
            <Button variant="ghost" onClick={() => setStep('summary')} className="w-full">
              View Summary ({platforms.length} platform{platforms.length > 1 ? 's' : ''})
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
